import React, { useState, useMemo, useCallback } from 'react';
import {
  Button,
  FeedOutlinedIcon,
  SettingsOutlinedIcon,
  Stack,
  CircularProgress
} from '@esgian/esgianui';
import BaselineChartSettingModal from '@components/Modals/BaselineChartSettingModal/BaselineChartSettingModal';
import CreateReportModal from '@components/Modals/ReportScheduler/CreateReportModal';
import { useSelector } from 'react-redux';
import { getMainPageRig, getMainPageSlice } from '@store/features/filters/MainPageSlice/MainPageSlice';
import { getLookupRigs } from '@store/features';
import { useTimezone } from '@hooks/useTimezone';
import { sendAndDownloadReport } from '@api/ReportScheduler';
import { logError } from '@api/Logging';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '@constants/index';
import moment from 'moment-timezone';

function BaselineCompActionButtons() {
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);

  const selectedRig = useSelector(getMainPageRig);
  const { startDate, endDate } = useSelector(getMainPageSlice);
  const lookupRigs = useSelector(getLookupRigs);
  const { selectedTimeZone } = useTimezone();

  const reportInitialData = useMemo(() => {
    if (!selectedRig) return null;
    const rigName = selectedRig.rigName || '';
    const periodDates = `${moment(startDate).format(DATE_FORMAT)} - ${moment(endDate).format(DATE_FORMAT)}`;
    return {
      rigId: selectedRig.rigId,
      reportType: 'RigOverview',
      period: 'Custom',
      periodStartDate: moment.tz(startDate, DATE_FORMAT, selectedTimeZone).startOf('day').format(DATE_TIME_FORMAT),
      periodEndDate: moment.tz(endDate, DATE_FORMAT, selectedTimeZone).endOf('day').format(DATE_TIME_FORMAT),
      reportName: `${rigName} - RigOverview - ${periodDates}`,
      isImmediate: true,
      visibility: 'user',
      options: { showTrend: true }
    };
  }, [selectedRig, startDate, endDate, selectedTimeZone]);

  const handleSendNow = useCallback((body) => {
    if (body.downloadImmediate) {
      setIsReportLoading(true);
      sendAndDownloadReport(null, body)
        .then(() => {
          setIsReportLoading(false);
        })
        .catch((error) => {
          logError(error, 'Failed to send and download report');
          setIsReportLoading(false);
        });
    }
  }, []);

  return (
    <Stack direction={'row'} spacing={1}>
      <Button
        variant={'text'}
        color={'secondary'}
        sx={{ color: (theme) => theme.palette.secondary.contrastText }}
        onClick={() => {
          setSettingsModalOpen(true);
        }}
        startIcon={<SettingsOutlinedIcon fontSize={'small'} color={'inherit'} />}>
        Chart Settings
      </Button>
      <Button
        variant={'text'}
        color={'secondary'}
        sx={{ color: (theme) => theme.palette.secondary.contrastText }}
        onClick={() => {
          setExportModalOpen(true);
        }}
        startIcon={isReportLoading ? <CircularProgress size={10} /> : <FeedOutlinedIcon color={'inherit'} />}>
        report
      </Button>
      {settingsModalOpen && (
        <BaselineChartSettingModal handleClose={() => setSettingsModalOpen(false)} />
      )}
      {exportModalOpen && (
        <CreateReportModal
          open={exportModalOpen}
          handleClose={() => { setExportModalOpen(false); }}
          lookupRigs={lookupRigs}
          initialData={reportInitialData}
          onSendNow={handleSendNow}
        />
      )}
    </Stack>
  );
}

BaselineCompActionButtons.propTypes = {};

BaselineCompActionButtons.defaultProps = {};

export default BaselineCompActionButtons;
